import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CashRate,
  TaxRateExpenseCurrency,
  CashRatePayload,
  createCashRate,
  deleteCashRate,
  getCashRates,
  updateCashRate,
} from '../services/api';
import { SortState, compareValues, formatDateShort, toggleSort } from '../utils/table';
import { useAuth } from '../context/AuthContext';
import { SheetsColumnMultiFilter } from '../components/SheetsColumnMultiFilter';
import '../styles/DataPage.css';
import './TaxesPage.css';

type CashRateSortKey = 'contract_currency' | 'expense_currency' | 'start_date' | 'end_date' | 'cash_rate';

type CashRatesFilterMenuKey = 'contract_currency' | 'expense_currency' | null;

const CURRENCY_OPTIONS: TaxRateExpenseCurrency[] = ['бн', 'ип', 'нал'];

interface CashRateFormState {
  contract_currency: TaxRateExpenseCurrency;
  expense_currency: TaxRateExpenseCurrency;
  start_date: string;
  end_date: string;
  cash_rate: string;
}

const EMPTY_FORM: CashRateFormState = {
  contract_currency: 'бн',
  expense_currency: 'бн',
  start_date: '',
  end_date: '',
  cash_rate: '',
};

function displayEndDate(value: string | null | undefined): string {
  return value ? formatDateShort(value) : '—';
}

function formatCashRate(value: number): string {
  return `${new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value)} %`;
}

function dateInRange(value: string | undefined | null, from: string, to: string): boolean {
  if (!value?.trim()) {
    return !from.trim() && !to.trim();
  }
  const hasFrom = Boolean(from.trim());
  const hasTo = Boolean(to.trim());
  if (!hasFrom && !hasTo) {
    return true;
  }
  const ts = new Date(value.slice(0, 10)).getTime();
  if (Number.isNaN(ts)) {
    return true;
  }
  const fromTs = hasFrom ? new Date(`${from}T00:00:00`).getTime() : Number.NEGATIVE_INFINITY;
  const toTs = hasTo ? new Date(`${to}T23:59:59`).getTime() : Number.POSITIVE_INFINITY;
  return ts >= fromTs && ts <= toTs;
}

function parseRateBound(value: string): number | null {
  const trimmed = value.trim().replace(',', '.');
  if (!trimmed) {
    return null;
  }
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : null;
}

function matchesSearch(row: CashRate, query: string): boolean {
  if (!query.trim()) {
    return true;
  }
  const haystack = [
    row.contract_currency,
    row.expense_currency,
    row.start_date,
    row.end_date,
    formatCashRate(row.cash_rate),
    String(row.cash_rate),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(query.trim().toLowerCase());
}

const CashRatesPage: React.FC = () => {
  const { can } = useAuth();
  const canView = can('MASTERDATA', 'EDIT');
  const canCreate = can('MASTERDATA', 'CREATE');
  const canEdit = can('MASTERDATA', 'EDIT');
  const canDelete = can('MASTERDATA', 'DELETE');

  const [rows, setRows] = useState<CashRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [formData, setFormData] = useState<CashRateFormState>(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState<CashRatesFilterMenuKey>(null);
  const [filterContractCurrencies, setFilterContractCurrencies] = useState<string[]>([]);
  const [filterCurrencies, setFilterCurrencies] = useState<string[]>([]);
  const [filterStartFrom, setFilterStartFrom] = useState('');
  const [filterStartTo, setFilterStartTo] = useState('');
  const [filterEndFrom, setFilterEndFrom] = useState('');
  const [filterEndTo, setFilterEndTo] = useState('');
  const [minRateFilter, setMinRateFilter] = useState('');
  const [maxRateFilter, setMaxRateFilter] = useState('');
  const [sortState, setSortState] = useState<SortState<CashRateSortKey>>({
    key: 'start_date',
    direction: 'desc',
  });

  const load = useCallback(async (): Promise<void> => {
    if (!canView) {
      setLoading(false);
      setRows([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await getCashRates();
      setRows(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка загрузки');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!filtersOpen) {
      setFilterMenuOpen(null);
    }
  }, [filtersOpen]);

  const selectedRow = useMemo(
    () => (selectedId == null ? null : rows.find((row) => row.id === selectedId) ?? null),
    [rows, selectedId],
  );

  useEffect(() => {
    if (selectedId != null && !rows.some((row) => row.id === selectedId)) {
      setSelectedId(null);
    }
  }, [rows, selectedId]);

  const currencyFilterOptions = useMemo(
    () => [...CURRENCY_OPTIONS],
    [],
  );

  const clearFilters = (): void => {
    setFilterContractCurrencies([]);
    setFilterCurrencies([]);
    setFilterStartFrom('');
    setFilterStartTo('');
    setFilterEndFrom('');
    setFilterEndTo('');
    setMinRateFilter('');
    setMaxRateFilter('');
    setFilterMenuOpen(null);
  };

  const hasActiveFilters =
    filterContractCurrencies.length > 0 ||
    filterCurrencies.length > 0 ||
    filterStartFrom.trim() !== '' ||
    filterStartTo.trim() !== '' ||
    filterEndFrom.trim() !== '' ||
    filterEndTo.trim() !== '' ||
    minRateFilter.trim() !== '' ||
    maxRateFilter.trim() !== '';

  const filteredRows = useMemo(() => {
    const minRate = parseRateBound(minRateFilter);
    const maxRate = parseRateBound(maxRateFilter);

    return rows.filter((row) => {
      const matchesContractCurrency =
        filterContractCurrencies.length === 0 || filterContractCurrencies.includes(row.contract_currency);
      const matchesCurrency =
        filterCurrencies.length === 0 || filterCurrencies.includes(row.expense_currency);
      const rate = Number(row.cash_rate) || 0;

      return (
        matchesSearch(row, searchText) &&
        matchesContractCurrency &&
        matchesCurrency &&
        dateInRange(row.start_date, filterStartFrom, filterStartTo) &&
        dateInRange(row.end_date, filterEndFrom, filterEndTo) &&
        (minRate === null || rate >= minRate) &&
        (maxRate === null || rate <= maxRate)
      );
    });
  }, [
    rows,
    searchText,
    filterContractCurrencies,
    filterCurrencies,
    filterStartFrom,
    filterStartTo,
    filterEndFrom,
    filterEndTo,
    minRateFilter,
    maxRateFilter,
  ]);

  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows];
    sorted.sort((left, right) => {
      const key = sortState.key;
      const leftValue = key === 'end_date'
        ? (left.end_date ?? '')
        : left[key];
      const rightValue = key === 'end_date'
        ? (right.end_date ?? '')
        : right[key];
      return compareValues(leftValue, rightValue, sortState.direction);
    });
    return sorted;
  }, [filteredRows, sortState]);

  useEffect(() => {
    if (selectedId != null && !sortedRows.some((row) => row.id === selectedId)) {
      setSelectedId(null);
    }
  }, [sortedRows, selectedId]);

  const openCreateModal = (): void => {
    setFormData(EMPTY_FORM);
    setFormError('');
    setModalMode('create');
    setModalOpen(true);
  };

  const openEditModal = (): void => {
    if (!selectedRow) {
      return;
    }
    setFormData({
      contract_currency: selectedRow.contract_currency,
      expense_currency: selectedRow.expense_currency,
      start_date: selectedRow.start_date,
      end_date: selectedRow.end_date ?? '',
      cash_rate: String(selectedRow.cash_rate),
    });
    setFormError('');
    setModalMode('edit');
    setModalOpen(true);
  };

  const closeModal = (): void => {
    if (saving) {
      return;
    }
    setModalOpen(false);
    setFormError('');
  };

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!formData.start_date.trim()) {
      setFormError('Укажите дату начала');
      return;
    }
    if (modalMode === 'create' && !formData.end_date.trim()) {
      setFormError('Укажите дату окончания');
      return;
    }
    if (formData.end_date && formData.start_date && formData.end_date < formData.start_date) {
      setFormError('Дата окончания не может быть раньше даты начала');
      return;
    }
    const rate = parseRateBound(formData.cash_rate);
    if (rate === null || rate < -100 || rate > 100) {
      setFormError('Укажите ставку кэша от -100 до 100');
      return;
    }

    setSaving(true);
    setFormError('');
    const payload: CashRatePayload = {
      contract_currency: formData.contract_currency,
      expense_currency: formData.expense_currency,
      start_date: formData.start_date,
      end_date: formData.end_date.trim() ? formData.end_date : null,
      cash_rate: rate,
    };

    try {
      if (modalMode === 'create') {
        const created = await createCashRate({
          ...payload,
          end_date: formData.end_date.trim(),
        });
        setRows((prev) => [...prev, created]);
        setSelectedId(created.id);
      } else if (selectedRow) {
        const updated = await updateCashRate(selectedRow.id, payload);
        setRows((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
      }
      setModalOpen(false);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!selectedRow || isDeleting) {
      return;
    }
    const confirmed = window.confirm(
      `Удалить ставку кэша ${selectedRow.contract_currency}/${selectedRow.expense_currency} `
      + `с ${formatDateShort(selectedRow.start_date)}?`,
    );
    if (!confirmed) {
      return;
    }
    setIsDeleting(true);
    setError('');
    try {
      await deleteCashRate(selectedRow.id);
      setRows((prev) => prev.filter((row) => row.id !== selectedRow.id));
      setSelectedId(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка удаления');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSort = (key: CashRateSortKey): void => {
    setSortState((current) => toggleSort(
      current,
      key,
      key === 'start_date' || key === 'end_date' ? 'desc' : 'asc',
    ));
  };

  return (
    <div className="data-page articles-page taxes-page cash-rates-page">
      {!canView ? (
        <div className="empty-state">Нет доступа к мастерданным (требуется право MASTERDATA:EDIT).</div>
      ) : (
        <>
          <div className="page-header-row">
            <h1>Кэш</h1>
            <div className="page-header-row__actions">
              {canCreate ? (
                <button type="button" className="create-button" onClick={openCreateModal}>
                  Создать
                </button>
              ) : null}
              {canEdit && selectedRow ? (
                <button type="button" className="secondary-button" onClick={openEditModal}>
                  Редактировать
                </button>
              ) : null}
              {canDelete && selectedRow ? (
                <button
                  type="button"
                  className="delete-row-button"
                  onClick={() => void handleDelete()}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Удаление…' : 'Удалить'}
                </button>
              ) : null}
              <button
                type="button"
                className={`filters-toggle ${!filtersOpen && hasActiveFilters ? 'filters-toggle--show-active-dot' : ''}`}
                onClick={() => setFiltersOpen((open) => !open)}
                aria-expanded={filtersOpen}
                aria-label={filtersOpen ? 'Скрыть фильтры' : 'Показать фильтры'}
              >
                Фильтры
                <span className="filters-toggle-badge" aria-hidden />
              </button>
            </div>
            <div className="page-header-row__search">
              <input
                type="text"
                className="search-input"
                placeholder="Поиск по текстовым полям"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>

          {error ? <div className="error-message">{error}</div> : null}

          <div className={`table-layout ${filtersOpen ? '' : 'table-layout--filters-collapsed'}`}>
            {filtersOpen && (
              <aside className="filters-panel">
                <div className="filters-header">
                  <h2>Фильтры</h2>
                  <button type="button" className="link-button" onClick={clearFilters}>
                    Сбросить
                  </button>
                </div>
                <div className="filters-grid">
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Валюта договора"
                      options={currencyFilterOptions}
                      selected={filterContractCurrencies}
                      onSelectedChange={setFilterContractCurrencies}
                      formatOption={(value) => value}
                      isOpen={filterMenuOpen === 'contract_currency'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'contract_currency' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Валюта расхода"
                      options={currencyFilterOptions}
                      selected={filterCurrencies}
                      onSelectedChange={setFilterCurrencies}
                      formatOption={(value) => value}
                      isOpen={filterMenuOpen === 'expense_currency'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'expense_currency' : null)}
                    />
                  </div>
                  <label>
                    Дата начала с
                    <input
                      type="date"
                      value={filterStartFrom}
                      onChange={(e) => setFilterStartFrom(e.target.value)}
                    />
                  </label>
                  <label>
                    Дата начала по
                    <input
                      type="date"
                      value={filterStartTo}
                      onChange={(e) => setFilterStartTo(e.target.value)}
                    />
                  </label>
                  <label>
                    Дата окончания с
                    <input
                      type="date"
                      value={filterEndFrom}
                      onChange={(e) => setFilterEndFrom(e.target.value)}
                    />
                  </label>
                  <label>
                    Дата окончания по
                    <input
                      type="date"
                      value={filterEndTo}
                      onChange={(e) => setFilterEndTo(e.target.value)}
                    />
                  </label>
                  <label>
                    Ставка от, %
                    <input
                      type="text"
                      inputMode="decimal"
                      value={minRateFilter}
                      onChange={(e) => setMinRateFilter(e.target.value)}
                    />
                  </label>
                  <label>
                    Ставка до, %
                    <input
                      type="text"
                      inputMode="decimal"
                      value={maxRateFilter}
                      onChange={(e) => setMaxRateFilter(e.target.value)}
                    />
                  </label>
                </div>
              </aside>
            )}

            <section className="table-content">
              {loading ? (
                <div className="loading">Загрузка...</div>
              ) : (
                <div className="table-panel">
                  <div className="table-scroll">
                    <table className="data-table taxes-page__table cash-rates-page__table">
                      <colgroup>
                        <col className="taxes-page__colgrid taxes-page__colgrid--currency" />
                        <col className="taxes-page__colgrid taxes-page__colgrid--currency" />
                        <col className="taxes-page__colgrid taxes-page__colgrid--date" />
                        <col className="taxes-page__colgrid taxes-page__colgrid--date" />
                        <col className="taxes-page__colgrid taxes-page__colgrid--rate" />
                      </colgroup>
                      <thead>
                        <tr>
                          <th
                            className="sortable"
                            onClick={() => handleSort('contract_currency')}
                            role="button"
                          >
                            Валюта договора
                          </th>
                          <th
                            className="sortable"
                            onClick={() => handleSort('expense_currency')}
                            role="button"
                          >
                            Валюта расхода
                          </th>
                          <th
                            className="sortable"
                            onClick={() => handleSort('start_date')}
                            role="button"
                          >
                            Дата начала
                          </th>
                          <th
                            className="sortable"
                            onClick={() => handleSort('end_date')}
                            role="button"
                          >
                            Дата окончания
                          </th>
                          <th
                            className="sortable col-num"
                            onClick={() => handleSort('cash_rate')}
                            role="button"
                          >
                            Кэш, %
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedRows.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="data-table__empty">
                              Ничего не найдено
                            </td>
                          </tr>
                        ) : (
                          sortedRows.map((row) => (
                            <tr
                              key={row.id}
                              className={`clickable-row${selectedId === row.id ? ' data-table__row--selected' : ''}`}
                              onClick={() => setSelectedId(row.id)}
                            >
                              <td>{row.contract_currency}</td>
                              <td>{row.expense_currency}</td>
                              <td>{formatDateShort(row.start_date)}</td>
                              <td>{displayEndDate(row.end_date)}</td>
                              <td className="col-num">{formatCashRate(row.cash_rate)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="table-panel__footer">
                    <table className="data-table data-table--footer taxes-page__table cash-rates-page__table">
                      <colgroup>
                        <col className="taxes-page__colgrid taxes-page__colgrid--currency" />
                        <col className="taxes-page__colgrid taxes-page__colgrid--currency" />
                        <col className="taxes-page__colgrid taxes-page__colgrid--date" />
                        <col className="taxes-page__colgrid taxes-page__colgrid--date" />
                        <col className="taxes-page__colgrid taxes-page__colgrid--rate" />
                      </colgroup>
                      <tfoot>
                        <tr>
                          <td>Итого: {sortedRows.length}</td>
                          <td />
                          <td />
                          <td />
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </section>
          </div>

          {modalOpen ? (
            <div className="taxes-page__modal-overlay" role="presentation" onClick={closeModal}>
              <div
                className="taxes-page__modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="taxes-modal-title"
                onClick={(e) => e.stopPropagation()}
              >
                <h2 id="taxes-modal-title">
                  {modalMode === 'create' ? 'Новая ставка кэша' : 'Редактирование ставки кэша'}
                </h2>
                <form className="taxes-page__form" onSubmit={(e) => void handleSubmit(e)}>
                  <label className="taxes-page__field">
                    <span>Валюта договора *</span>
                    <select
                      value={formData.contract_currency}
                      onChange={(e) => setFormData((prev) => ({
                        ...prev,
                        contract_currency: e.target.value as TaxRateExpenseCurrency,
                      }))}
                      disabled={saving}
                    >
                      {CURRENCY_OPTIONS.map((value) => (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="taxes-page__field">
                    <span>Валюта расхода *</span>
                    <select
                      value={formData.expense_currency}
                      onChange={(e) => setFormData((prev) => ({
                        ...prev,
                        expense_currency: e.target.value as TaxRateExpenseCurrency,
                      }))}
                      disabled={saving}
                    >
                      {CURRENCY_OPTIONS.map((value) => (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="taxes-page__field">
                    <span>Дата начала *</span>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData((prev) => ({ ...prev, start_date: e.target.value }))}
                      disabled={saving}
                      required
                    />
                  </label>
                  <label className="taxes-page__field">
                    <span>Дата окончания{modalMode === 'create' ? ' *' : ''}</span>
                    <input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData((prev) => ({
                        ...prev,
                        end_date: e.target.value,
                      }))}
                      disabled={saving}
                      required={modalMode === 'create'}
                    />
                  </label>
                  <label className="taxes-page__field">
                    <span>Кэш, % *</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="taxes-page__rate-input"
                      value={formData.cash_rate}
                      onChange={(e) => setFormData((prev) => ({
                        ...prev,
                        cash_rate: e.target.value,
                      }))}
                      disabled={saving}
                      required
                    />
                  </label>

                  {formError ? <div className="error-message">{formError}</div> : null}

                  <div className="taxes-page__modal-actions">
                    <button type="button" className="secondary-button" onClick={closeModal} disabled={saving}>
                      Отмена
                    </button>
                    <button type="submit" className="create-button" disabled={saving}>
                      {saving ? 'Сохранение…' : 'Сохранить'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default CashRatesPage;
